/**
 * Route tree assembly (foundation-owned). Each area exports `<area>Routes` from src/areas/<area>/routes.tsx;
 * adding a route = add it to that array, nothing to change here.
 *
 * Why code-based routing (not file-based): 8 agents work in parallel; a generated routeTree.gen.ts would be one
 * shared file every route change rewrites. Code-based keeps each area's routes in the area's own file, still fully
 * typed (`<Link to>` checks paths), with lazy board components for code-splitting.
 */
import { createRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { creationIndexRoute, indexRoute, publicRoute, referenceIndexRoute, rootRoute, RouteError, staffRoute } from "./route-base";
import { platformRoutes } from "@/areas/saas/platform";
import { authPublicRoutes } from "@/areas/auth/routes";
import { creationRoutes } from "@/areas/creation/routes";
import { clientsRoutes } from "@/areas/clients/routes";
import { operationsRoutes } from "@/areas/operations/routes";
import { communicationRoutes } from "@/areas/communication/routes";
import { financeRoutes } from "@/areas/finance/routes";
import { accessPublicRoutes, accessRoutes } from "@/areas/access/routes";
import { saasRoutes } from "@/areas/saas/routes";
import { techRoutes } from "@/areas/tech/routes";

export const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([...authPublicRoutes, ...accessPublicRoutes]),
  staffRoute.addChildren([
    indexRoute,
    referenceIndexRoute,
    creationIndexRoute,
    ...creationRoutes,
    ...clientsRoutes,
    ...operationsRoutes,
    ...communicationRoutes,
    ...financeRoutes,
    ...accessRoutes,
    ...saasRoutes,
    ...platformRoutes,
    ...techRoutes,
  ]),
]);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: (n, e) => n < 2 && !(e && typeof e === "object" && "status" in e && [401, 403, 404].includes((e as { status: number }).status)), refetchOnWindowFocus: false },
  },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultErrorComponent: RouteError,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
